import { createdBoxQueries } from '@/entities/sets/created';
import useVisible from '@/shared/hooks/useVisible';
import { useModalStore } from '@/shared/lib/store/model/modal.store';
import { ErrorPopup, Modal } from '@/shared/ui';
import { showToast } from '@/shared/utils/toast';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useNavigate, useLocation } from '@tanstack/react-router';

interface CreatedDeleteButtonProps {
    showTooltip?: boolean;
    tooltipText?: string;
    buttonText?: string;
    styles: {
        button?: string;
        tooltip?: string;
    };
    onRefetch?: () => void;
    checkList?: string[];
}

export function CreatedDeleteButton({
    showTooltip = false,
    tooltipText = '삭제',
    buttonText,
    styles,
    onRefetch,
    checkList,
}: CreatedDeleteButtonProps) {
    const navigate = useNavigate({ from: '/sets/created-box' });
    const location = useLocation();
    const [isVisible, setIsVisible] = useVisible();
    const { showModal } = useModalStore();

    const deleteCreatedBox = createdBoxQueries.postDeleteCreated();

    const onDelete = () => {
        deleteCreatedBox.mutate(
            {
                examIdList: checkList ?? [],
            },
            {
                onSuccess: (res) => {
                    if (res?.resultOk) {
                        showToast('선택한 자료를 삭제했습니다.');
                        refetchHandler();
                        goToCreatedBox();
                    } else {
                        alert('삭제 실패');
                    }
                },
                onError: () => {
                    showModal({
                        isCustom: true,
                        id: 'error',
                        initContents: () => <ErrorPopup />,
                    });
                },
            }
        );
    };

    const refetchHandler = () => {
        if (onRefetch) {
            onRefetch();
        }
    };

    const goToCreatedBox = () => {
        if (location.pathname !== '/sets/created-box') {
            navigate({ to: '/sets/created-box' });
        }
    };

    const onModalVisible = () => {
        if (checkList && checkList.length > 0) {
            setIsVisible();
        }
    };

    return (
        <>
            <Modal
                open={isVisible}
                onClose={setIsVisible}
                title="안내"
                description="선택한 자료를 삭제하시겠습니까?"
                onConfirm={onDelete}
                onConfirmText="삭제"
                alert={true}
            />
            {showTooltip ? (
                <Tooltip.Provider>
                    <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                            <button
                                type="button"
                                className={styles.button}
                                onClick={onModalVisible}
                            >
                                <span className="tooltip">{tooltipText}</span>
                                {buttonText && buttonText}
                            </button>
                        </Tooltip.Trigger>
                    </Tooltip.Root>
                </Tooltip.Provider>
            ) : (
                <button type="button" className={styles.button} onClick={onModalVisible}>
                    {buttonText}
                </button>
            )}
        </>
    );
}
